/* ═══════════════════════════════════════════════════════════════
   Collab binding — how a tiptap canvas attaches to a shared Yjs document
   (docs/SPEC_WORKFLOW.md §5). Shared by the Spec Studio canvas (masters)
   and the Designer canvas (project specs) so both bind IDENTICALLY:
   ProseMirror's History is swapped for the Yjs undo manager, and every
   peer's caret shows in their cursor-token colour with their name.
   ═══════════════════════════════════════════════════════════════ */

import type { Extensions } from '@tiptap/core';
import { Collaboration } from '@tiptap/extension-collaboration';
import { CollaborationCursor } from '@tiptap/extension-collaboration-cursor';
import type * as Y from 'yjs';
import type { HocuspocusProvider } from '@hocuspocus/provider';

/** The Yjs fragment every shared spec document lives in. */
export const COLLAB_FIELD = 'content';

/** What a canvas needs to bind to a shared document. */
export interface SpecCanvasCollab {
  ydoc: Y.Doc;
  provider: HocuspocusProvider;
  user: { name: string; color: string; slot: number };
}

/** A peer's caret: class-coloured from the cursor tokens - never an inline
 *  colour (DESIGN: tokens only). */
export function renderCollabCaret(user: Record<string, unknown>): HTMLElement {
  const slot = typeof user.slot === 'number' ? user.slot : 1;
  const caret = document.createElement('span');
  caret.className = `collaboration-cursor__caret collab-c${slot}`;
  const label = document.createElement('span');
  label.className = 'collaboration-cursor__label';
  label.textContent = typeof user.name === 'string' ? user.name : '';
  caret.appendChild(label);
  return caret;
}

export function collabSelection(user: Record<string, unknown>): { class: string } {
  const slot = typeof user.slot === 'number' ? user.slot : 1;
  return { class: `collaboration-cursor__selection collab-sel-c${slot}` };
}

/** The two extensions that bind a canvas to the shared document. */
export function collabExtensions(collab: SpecCanvasCollab): Extensions {
  return [
    Collaboration.configure({ document: collab.ydoc, field: COLLAB_FIELD }),
    CollaborationCursor.configure({
      provider: collab.provider,
      user: collab.user,
      render: renderCollabCaret,
      selectionRender: collabSelection,
    }),
  ];
}

/** A shared document's undo is the Yjs undo manager - ProseMirror's History
 *  would undo OTHER people's edits, so it is dropped when bound. */
export function withoutHistory<T extends { name: string }>(extensions: T[]): T[] {
  return extensions.filter((ext) => ext.name !== 'history');
}
